import { useState, useEffect, useRef } from 'react';
import {
  Plus, X, Eye, ChevronLeft, ChevronRight, Loader2, Trash2, Image as ImageIcon
} from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent } from './ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { storyAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { devError } from '../lib/devLog';

const COLORS = ['#1e293b', '#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#ec4899', '#0ea5e9'];
const MAX_IMAGE_MB = 5;

const StoriesBar = () => {
  const { user } = useAuth();
  const [storyGroups, setStoryGroups] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showViewer, setShowViewer] = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [newContent, setNewContent] = useState('');
  const [selectedColor, setSelectedColor] = useState('#1e293b');
  const [imageData, setImageData] = useState(null);
  const [creating, setCreating] = useState(false);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    fetchStories();
    const interval = setInterval(fetchStories, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStories = async () => {
    try {
      const res = await storyAPI.getAll();
      setStoryGroups(res.data);
    } catch (e) { devError(e); }
  };

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      toast.error(`Image too large (max ${MAX_IMAGE_MB}MB)`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImageData(reader.result);
    reader.onerror = () => toast.error('Failed to read image');
    reader.readAsDataURL(file);
  };

  const resetCreate = () => {
    setNewContent('');
    setImageData(null);
    setSelectedColor('#1e293b');
  };

  const handleCreate = async () => {
    if (!newContent.trim() && !imageData) {
      toast.error('Add text or an image');
      return;
    }
    setCreating(true);
    try {
      await storyAPI.create({
        content: newContent.trim(),
        type: imageData ? 'image' : 'text',
        background_color: selectedColor,
        image_data: imageData,
      });
      setShowCreate(false);
      resetCreate();
      fetchStories();
      toast.success('Story posted!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post story');
    } finally { setCreating(false); }
  };

  const openStory = (group, idx = 0) => {
    setActiveGroup(group);
    setActiveIndex(idx);
    setShowViewer(true);
    // Mark first story as viewed
    if (group.stories[idx]) {
      storyAPI.view(group.stories[idx].id).catch(() => {});
    }
    startTimer(group, idx);
  };

  const startTimer = (group, idx) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => nextStory(group, idx), 5000);
  };

  const nextStory = (group, idx) => {
    if (!group) return;
    const nextIdx = idx + 1;
    if (nextIdx < group.stories.length) {
      setActiveIndex(nextIdx);
      storyAPI.view(group.stories[nextIdx].id).catch(() => {});
      startTimer(group, nextIdx);
    } else {
      // Move to next group
      const currentGroupIdx = storyGroups.indexOf(group);
      if (currentGroupIdx < storyGroups.length - 1) {
        const nextGroup = storyGroups[currentGroupIdx + 1];
        setActiveGroup(nextGroup);
        setActiveIndex(0);
        storyAPI.view(nextGroup.stories[0].id).catch(() => {});
        startTimer(nextGroup, 0);
      } else {
        setShowViewer(false);
      }
    }
  };

  const prevStory = () => {
    if (activeIndex > 0) {
      const newIdx = activeIndex - 1;
      setActiveIndex(newIdx);
      startTimer(activeGroup, newIdx);
    }
  };

  const handleDeleteStory = async (storyId) => {
    try {
      await storyAPI.delete(storyId);
      fetchStories();
      setShowViewer(false);
      toast.success('Story deleted');
    } catch (e) { toast.error('Failed to delete'); }
  };

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const hasUnviewedStories = (group) => group.stories.some(s => !s.has_viewed);

  return (
    <>
      {/* Stories horizontal scroll */}
      <div className="mb-4">
        <div ref={scrollRef} className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" data-testid="stories-bar">
          {/* Create Story Button */}
          <button
            onClick={() => setShowCreate(true)}
            className="flex-shrink-0 flex flex-col items-center gap-1"
            data-testid="create-story-btn"
          >
            <div className="w-16 h-16 rounded-full border-2 border-dashed border-orange-300 flex items-center justify-center bg-orange-50 hover:bg-orange-100 transition-colors">
              <Plus className="w-6 h-6 text-orange-500" />
            </div>
            <span className="text-xs text-zinc-500 font-medium">Your Story</span>
          </button>

          {/* Story circles */}
          {storyGroups.map((group) => (
            <button
              key={group.user_id}
              onClick={() => openStory(group)}
              className="flex-shrink-0 flex flex-col items-center gap-1"
              data-testid={`story-group-${group.user_id}`}
            >
              <div className={`w-16 h-16 rounded-full p-0.5 ${
                hasUnviewedStories(group) ? 'bg-gradient-to-br from-orange-500 to-pink-500' : 'bg-zinc-300'
              }`}>
                <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-lg font-bold text-zinc-600">
                  {group.user_name?.charAt(0)?.toUpperCase()}
                </div>
              </div>
              <span className="text-xs text-zinc-600 font-medium truncate max-w-[64px]">
                {group.user_id === user?.id ? 'You' : group.user_name?.split(' ')[0]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Create Story Dialog */}
      <Dialog open={showCreate} onOpenChange={(v) => { setShowCreate(v); if (!v) resetCreate(); }}>
        <DialogContent className="sm:max-w-sm p-0 overflow-hidden">
          <div
            className="p-6 min-h-[350px] flex flex-col justify-between transition-colors duration-300 relative"
            style={{ backgroundColor: imageData ? '#000' : selectedColor }}
          >
            {/* Background image (if picked) */}
            {imageData && (
              <img
                src={imageData}
                alt="Story preview"
                className="absolute inset-0 w-full h-full object-cover opacity-90"
                data-testid="story-image-preview"
              />
            )}
            <div className="flex justify-between items-start relative z-10">
              <p className="text-white/80 text-sm font-medium drop-shadow">Create Story</p>
              <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)} className="text-white/80 hover:text-white">
                <X className="w-4 h-4" />
              </Button>
            </div>

            <Textarea
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              placeholder={imageData ? 'Add a caption (optional)' : "What's on your mind?"}
              className="bg-transparent border-none text-white text-lg placeholder:text-white/60 focus-visible:ring-0 resize-none text-center min-h-[120px] relative z-10 drop-shadow"
              maxLength={280}
              data-testid="story-content-input"
            />

            <div className="space-y-3 relative z-10">
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleImagePick}
                className="hidden"
                data-testid="story-image-input"
              />
              {imageData ? (
                <div className="flex justify-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setImageData(null)}
                    className="text-white/90 hover:text-white bg-black/30 hover:bg-black/50 backdrop-blur-sm"
                    data-testid="remove-story-image-btn"
                  >
                    <X className="w-4 h-4 mr-1" /> Remove image
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2 justify-center items-center">
                  {COLORS.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setSelectedColor(c)}
                      className={`w-7 h-7 rounded-full transition-transform ${selectedColor === c ? 'scale-125 ring-2 ring-white ring-offset-2' : ''}`}
                      style={{ backgroundColor: c, ringOffsetColor: selectedColor }}
                      aria-label={`Background color ${c}`}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="ml-2 w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                    title="Add photo"
                    data-testid="pick-story-image-btn"
                  >
                    <ImageIcon className="w-4 h-4 text-white" />
                  </button>
                </div>
              )}

              <Button
                onClick={handleCreate}
                disabled={(!newContent.trim() && !imageData) || creating}
                className="w-full bg-white/20 hover:bg-white/30 text-white backdrop-blur-sm"
                data-testid="post-story-btn"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Share Story
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Story Viewer */}
      <Dialog open={showViewer} onOpenChange={(v) => { setShowViewer(v); if (!v && timerRef.current) clearTimeout(timerRef.current); }}>
        <DialogContent className="sm:max-w-sm p-0 overflow-hidden h-[600px]">
          {activeGroup && activeGroup.stories[activeIndex] && (() => {
            const story = activeGroup.stories[activeIndex];
            return (
              <div
                className="w-full h-full flex flex-col justify-between relative"
                style={{ backgroundColor: story.image_data ? '#000' : (story.background_color || '#1e293b') }}
              >
                {/* Background image (if image story) */}
                {story.image_data && (
                  <img
                    src={story.image_data}
                    alt="Story"
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid="story-image-view"
                  />
                )}
                {/* Subtle gradient to keep header/caption legible over the image */}
                {story.image_data && (
                  <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60 pointer-events-none" />
                )}
                {/* Progress bars */}
                <div className="flex gap-1 p-3 pb-0 relative z-10">
                  {activeGroup.stories.map((s, i) => (
                    <div key={s.id} className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${
                          i < activeIndex ? 'bg-white w-full' :
                          i === activeIndex ? 'bg-white w-full animate-[progress_5s_linear]' :
                          'bg-transparent w-0'
                        }`}
                      />
                    </div>
                  ))}
                </div>

                {/* Header */}
                <div className="flex items-center justify-between p-3 relative z-10">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {activeGroup.user_name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">{activeGroup.user_name}</p>
                      <p className="text-white/50 text-xs">{new Date(story.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {activeGroup.user_id === user?.id && (
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteStory(story.id)} className="text-white/70 hover:text-white">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => { setShowViewer(false); if (timerRef.current) clearTimeout(timerRef.current); }} className="text-white/70 hover:text-white">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Content */}
                <div className={`flex-1 flex items-center justify-center px-8 relative z-10 ${story.image_data ? 'items-end pb-12' : ''}`}>
                  {story.content && (
                    <p className="text-white text-xl font-medium text-center leading-relaxed drop-shadow-lg">{story.content}</p>
                  )}
                </div>

                {/* Navigation */}
                <div className="absolute inset-y-0 left-0 w-1/3 cursor-pointer z-20" onClick={prevStory} />
                <div className="absolute inset-y-0 right-0 w-1/3 cursor-pointer z-20" onClick={() => nextStory(activeGroup, activeIndex)} />

                {/* Footer */}
                <div className="p-3 flex items-center justify-between relative z-10">
                  <div className="flex items-center gap-1 text-white/50 text-xs">
                    <Eye className="w-3 h-3" />
                    <span>{story.view_count} views</span>
                  </div>
                  <div className="flex gap-1">
                    {activeIndex > 0 && (
                      <Button variant="ghost" size="sm" onClick={prevStory} className="text-white/50 hover:text-white">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                    )}
                    {activeIndex < activeGroup.stories.length - 1 && (
                      <Button variant="ghost" size="sm" onClick={() => nextStory(activeGroup, activeIndex)} className="text-white/50 hover:text-white">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StoriesBar;
